import type { ChatWidgetProps, ChatWidgetSettings, MountAPI, WidgetPosition } from './types';
import type {
  RevenueCaptureConfigResponse,
  RevenueCaptureEmbedOverrides,
  RevenueCaptureMountOptions,
  RevenueCaptureMountProps,
  RevenueCapturePageContext,
  RevenueCapturePageContextSource,
  RevenueCapturePropsOptions,
} from './types';
import { mount } from './mount';
import { DEFAULT_THEME } from './constants';
import { isSafeUrl } from './utils/urlSafety';

const VALID_POSITIONS: WidgetPosition[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function trimString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isValidPosition(value: unknown): value is WidgetPosition {
  return typeof value === 'string' && VALID_POSITIONS.includes(value as WidgetPosition);
}

function mergeRecords(...records: Array<Record<string, unknown> | null | undefined>) {
  return records.reduce<Record<string, unknown>>((acc, current) => {
    if (!isPlainObject(current)) return acc;
    return { ...acc, ...current };
  }, {});
}

function withPageContext(
  settings: ChatWidgetSettings | null | undefined,
  pageContext: RevenueCapturePageContext
): ChatWidgetSettings {
  const base = isPlainObject(settings) ? settings : {};
  return {
    ...base,
    sessionVariables: mergeRecords(
      isPlainObject(base.sessionVariables)
        ? (base.sessionVariables as Record<string, unknown>)
        : null,
      {
        pageContext,
      }
    ),
  };
}

function applySafeOverrides(
  settings: ChatWidgetSettings,
  overrides?: RevenueCaptureEmbedOverrides
): ChatWidgetSettings {
  if (!overrides) return settings;

  const nextSettings: ChatWidgetSettings = { ...settings };

  if (isValidPosition(overrides.position)) {
    nextSettings.behavior = {
      ...(nextSettings.behavior || {}),
      position: overrides.position,
    };
  }

  const primaryColor = trimString(overrides.primaryColor);
  if (primaryColor) {
    nextSettings.theme = {
      ...DEFAULT_THEME,
      ...(nextSettings.theme || {}),
      primary: primaryColor,
    };
  }

  const calendlyUrl = trimString(overrides.calendlyUrl);
  if (calendlyUrl && isSafeUrl(calendlyUrl)) {
    nextSettings.handoff = {
      ...(nextSettings.handoff || {}),
      enabled: true,
      mode: 'link',
      url: calendlyUrl,
    };
  }

  return nextSettings;
}

export function buildRevenueCapturePageContext(
  source: RevenueCapturePageContextSource = {}
): RevenueCapturePageContext {
  const hasWindow = typeof window !== 'undefined';
  const hasDocument = typeof document !== 'undefined';
  const fallbackOrigin =
    trimString(source.origin) || (hasWindow ? trimString(window.location.origin) : '');
  const rawUrl = trimString(source.url) || (hasWindow ? trimString(window.location.href) : '');

  let parsedUrl: URL | null = null;
  if (rawUrl) {
    try {
      parsedUrl = fallbackOrigin ? new URL(rawUrl, fallbackOrigin) : new URL(rawUrl);
    } catch {
      parsedUrl = null;
    }
  }

  const rawSearch = trimString(source.search) || parsedUrl?.search || '';
  const searchParams = new URLSearchParams(rawSearch);
  const query: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (query[key] === undefined) {
      query[key] = value;
    }
  });

  return {
    url: parsedUrl?.href || rawUrl,
    pathname: trimString(source.pathname) || parsedUrl?.pathname || '',
    title: source.title ?? (hasDocument ? document.title : ''),
    referrer: source.referrer ?? (hasDocument ? document.referrer : ''),
    query,
    utm: {
      source: query.utm_source || '',
      medium: query.utm_medium || '',
      campaign: query.utm_campaign || '',
      term: query.utm_term || '',
      content: query.utm_content || '',
    },
  };
}

function normalizeMountProps(config: RevenueCaptureConfigResponse): RevenueCaptureMountProps {
  return isPlainObject(config.mountProps) ? (config.mountProps as RevenueCaptureMountProps) : {};
}

export function createRevenueCaptureMountProps(
  options: RevenueCapturePropsOptions
): ChatWidgetProps {
  const config = options.config || {};
  const pageContext = options.pageContext || buildRevenueCapturePageContext();
  const configProps = normalizeMountProps(config);
  const localProps = isPlainObject(options.props) ? options.props : {};
  const settings = applySafeOverrides(
    withPageContext(config.settings, pageContext),
    options.overrides
  );
  const widgetAuthToken = trimString(options.widgetAuthToken);

  const nextProps: ChatWidgetProps = {
    ...configProps,
    ...localProps,
    settings,
  };

  if (widgetAuthToken) {
    nextProps.widgetAuthToken = widgetAuthToken;
  }

  const sessionVariables = mergeRecords(
    isPlainObject(configProps.sessionVariables)
      ? (configProps.sessionVariables as Record<string, unknown>)
      : null,
    isPlainObject(localProps.sessionVariables)
      ? (localProps.sessionVariables as Record<string, unknown>)
      : null,
    { pageContext }
  );
  if (Object.keys(sessionVariables).length > 0) {
    nextProps.sessionVariables = sessionVariables;
  }

  const customFields = mergeRecords(
    isPlainObject(configProps.customFields)
      ? (configProps.customFields as Record<string, unknown>)
      : null,
    isPlainObject(localProps.customFields)
      ? (localProps.customFields as Record<string, unknown>)
      : null
  );
  if (Object.keys(customFields).length > 0) {
    nextProps.customFields = customFields;
  }

  return nextProps;
}

export function mountRevenueCaptureWidget(options: RevenueCaptureMountOptions): MountAPI {
  const { container, ...rest } = options;
  return mount({
    container,
    props: createRevenueCaptureMountProps(rest),
  });
}
